import { isCompanionCtaImplemented } from '@/components/companion/cta/companion-cta-registry';
import { useCompanionCtaNavigation } from '@/components/companion/cta/useCompanionCtaNavigation';
import { CompanionDismissChips } from '@/components/companion/feed/CompanionDismissChips';
import { CompanionDismissedNotice } from '@/components/companion/feed/CompanionDismissedNotice';
import {
  COMPANION_PANEL_ID,
  focusCompanionTrigger,
} from '@/components/companion/feed/companion-panel-ids';
import { CompanionPanelEmptyState } from '@/components/companion/feed/CompanionPanelEmptyState';
import { CompanionPanelHeader } from '@/components/companion/feed/CompanionPanelHeader';
import { CompanionWhyPanel } from '@/components/companion/feed/CompanionWhyPanel';
import {
  resolveCompanionFeedState,
  visibleCompanionActions,
} from '@/components/companion/feed/companion-feed-state';
import { resolveCompanionPresenter } from '@/components/companion/feed/presenters';
import { useCompanionActions } from '@/hooks/actions/use-companion-actions';
import { useCompanionPanelStore, type CompanionPanelScope } from '@/stores/companion-panel-store';
import { Box, Button, Stack, Text } from '@chakra-ui/react';
import type {
  CompanionActionDto,
  CompanionDismissReason,
  CompanionFeedDto,
  CompanionUndoOfferDto,
} from '@fluenceur/types';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The companion's panel: one card at a time, next to the page rather than over
 * it.
 *
 * Non-modal on purpose. There is no backdrop, no focus trap and nothing behind
 * it is disabled: the panel is a suggestion the brand can ignore while carrying
 * on, not a decision it has to make first. Escape closes it and gives the
 * keyboard back to the trigger, which is the only thing a modal would have done
 * better.
 */

/** Below the banners (1300) and above the top bar (1200). */
const PANEL_Z_INDEX = 1250;
const DEFAULT_TOP_OFFSET = 68;

interface CompanionPanelProps {
  scope: CompanionPanelScope;
  brandId: string;
  feed: CompanionFeedDto;
  onClose: () => void;
  /** Distance from the top of the viewport, banners and top bar included. */
  top?: number;
  /** Set when the panel opened because someone asked for it, never on restore. */
  autoFocus?: boolean;
}

type DismissedView = {
  actionId: string;
  reason: CompanionDismissReason;
  snoozeDays: number | null;
  undo: CompanionUndoOfferDto | null;
  version: number;
};

export const CompanionPanel = ({
  scope,
  brandId,
  feed,
  onClose,
  top = DEFAULT_TOP_OFFSET,
  autoFocus = false,
}: CompanionPanelProps) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const actions = useCompanionActions(brandId);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const storedIndex = useCompanionPanelStore((state) => state.byScope[scope]?.index ?? 0);
  const whyOpenFor = useCompanionPanelStore((state) => state.byScope[scope]?.whyOpenFor ?? null);
  const setIndex = useCompanionPanelStore((state) => state.setIndex);
  const setWhyOpenFor = useCompanionPanelStore((state) => state.setWhyOpenFor);

  const [dismissing, setDismissing] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<DismissedView | null>(null);

  const visible = visibleCompanionActions(feed);
  const visibleIdsKey = visible.map((action) => action.id).join('|');
  const index = Math.min(storedIndex, Math.max(0, visible.length - 1));
  const currentAction: CompanionActionDto | undefined = visible[index];
  const currentActionId = currentAction?.id ?? null;
  const currentActionVersion = currentAction?.version ?? null;

  const { openCta, isPending: isCtaPending } = useCompanionCtaNavigation({ onNavigated: onClose });

  // Opening the panel is a gesture of its own: it is what the daily-usage
  // measure counts, and it is recorded even when there is nothing to read —
  // "the brand looked and found nothing" is exactly what that measure needs.
  useEffect(() => {
    actionsRef.current.recordPanelView();
  }, []);

  // The deck changed under the reader: start again at the first card rather
  // than land them on whatever now sits at the position they had.
  useEffect(() => {
    useCompanionPanelStore
      .getState()
      .syncVisibleIds(scope, visibleIdsKey ? visibleIdsKey.split('|') : []);
  }, [scope, visibleIdsKey]);

  // One `seen` per card per exposure. The store answers whether this session
  // already reported it, so walking back and forth in the deck costs nothing.
  useEffect(() => {
    if (!currentActionId || currentActionVersion === null) return;
    if (!useCompanionPanelStore.getState().markSeenLocally(scope, currentActionId)) return;
    actionsRef.current.markSeen(currentActionId, currentActionVersion);
  }, [scope, currentActionId, currentActionVersion]);

  useEffect(() => {
    if (!autoFocus) return;
    panelRef.current?.focus();
  }, [autoFocus]);

  const close = useCallback(() => {
    onClose();
    focusCompanionTrigger();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close();
    },
    [close]
  );

  const goTo = useCallback(
    (next: number) => {
      setDismissing(null);
      setDismissed(null);
      setIndex(scope, next);
    },
    [scope, setIndex]
  );

  const handleDismiss = useCallback(
    async (reason: CompanionDismissReason) => {
      if (!currentAction) return;
      const result = await actionsRef.current.dismiss({ action: currentAction, reason });
      setDismissing(null);
      if (!result) return;
      setDismissed({
        actionId: currentAction.id,
        reason,
        snoozeDays: currentAction.snoozeOffer?.days ?? null,
        undo: result.undo,
        version: result.action.version,
      });
    },
    [currentAction]
  );

  const handleUndo = useCallback(async () => {
    if (!dismissed?.undo) return;
    const undone = await actionsRef.current.undo({
      actionId: dismissed.actionId,
      version: dismissed.version,
      eventId: dismissed.undo.eventId,
    });
    if (undone) setDismissed(null);
  }, [dismissed]);

  const feedState = resolveCompanionFeedState(feed);
  const Presenter = currentAction ? resolveCompanionPresenter(currentAction.kind) : null;
  const ctaAvailable = currentAction
    ? isCompanionCtaImplemented(currentAction.cta.workflow)
    : false;
  const isWhyOpen = Boolean(currentAction && whyOpenFor === currentAction.id);

  return (
    <Box
      as="section"
      ref={panelRef}
      id={COMPANION_PANEL_ID}
      data-testid="companion-panel"
      role="region"
      tabIndex={-1}
      aria-label={t('companion.panel.title')}
      onKeyDown={handleKeyDown}
      position="fixed"
      top={`${top}px`}
      right="16px"
      width="min(380px, calc(100vw - 32px))"
      maxH={`calc(100vh - ${top + 16}px)`}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      bg="panelBg"
      borderWidth="1px"
      borderColor="cardBorder"
      borderRadius="xl"
      zIndex={PANEL_Z_INDEX}
      _focusVisible={{ outline: 'none' }}
    >
      <CompanionPanelHeader
        mood={
          feedState === 'allHandled' ? 'happy' : feedState === 'opportunities' ? 'alert' : 'doze'
        }
        index={visible.length > 0 ? index + 1 : null}
        total={visible.length}
        onPrevious={() => goTo(index - 1)}
        onNext={() => goTo(index + 1)}
      />

      {isCtaPending ? (
        <Text
          px="18px"
          py="10px"
          bg="softCream"
          color="ink"
          fontSize="13.5px"
          fontWeight="600"
          data-testid="companion-panel-launched"
        >
          {t('companion.panel.launched')}
        </Text>
      ) : null}

      <Box overflowY="auto" flex={1} minH={0}>
        {dismissed ? (
          <CompanionDismissedNotice
            reason={dismissed.reason}
            snoozeDays={dismissed.snoozeDays}
            undo={dismissed.undo}
            onUndo={handleUndo}
            isUndoing={actions.isUndoing}
          />
        ) : null}

        {!dismissed && currentAction && Presenter ? (
          <Stack gap="0">
            <Presenter
              action={currentAction}
              onOpenCta={() => {
                if (!ctaAvailable) return;
                void openCta(currentAction);
              }}
              onDismiss={
                dismissing === currentAction.id ? undefined : () => setDismissing(currentAction.id)
              }
              isCtaPending={isCtaPending}
              isCtaDisabled={!ctaAvailable}
              footerEnd={
                <Button
                  variant="plain"
                  h="36px"
                  px="0"
                  fontFamily="heading"
                  fontSize="13.5px"
                  fontWeight="600"
                  color="inkMuted"
                  textDecoration="underline"
                  _hover={{ color: 'ink' }}
                  onClick={() => setWhyOpenFor(scope, isWhyOpen ? null : currentAction.id)}
                >
                  {t(isWhyOpen ? 'companion.panel.why.hide' : 'companion.panel.why.show')}
                </Button>
              }
            />

            {isWhyOpen ? (
              <Box px="18px" pb="16px">
                <CompanionWhyPanel action={currentAction} />
              </Box>
            ) : null}

            {dismissing === currentAction.id ? (
              <Box px="18px" pb="16px">
                <CompanionDismissChips
                  snoozeOffer={currentAction.snoozeOffer}
                  onChoose={(reason) => void handleDismiss(reason)}
                  onCancel={() => setDismissing(null)}
                  isPending={actions.isDismissing}
                />
              </Box>
            ) : null}
          </Stack>
        ) : null}

        {!dismissed && !currentAction ? (
          <CompanionPanelEmptyState
            state={feedState === 'opportunities' ? 'zero' : feedState}
            feed={feed}
          />
        ) : null}
      </Box>
    </Box>
  );
};
